// Adding a copy of a document from a reply button under Mor's message: the button opens the
// file picker, and the picked file is uploaded as "just me" with no form.

import { createElement, useRef, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import { fileProblem } from '../components/AddFile.js';

export type UploadChoice = {
  /** The hidden file input; render it once. */
  input: JSX.Element;
  /** Opens the picker. Call it from the click handler itself, with nothing awaited first. */
  press: () => void;
  busy: boolean;
  problem: string | null;
};

export function useUploadChoice({
  client,
  planId,
  threadId,
}: {
  client: ApiClient;
  planId: string;
  threadId: string | null;
}): UploadChoice {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const press = (): void => {
    setProblem(null);
    inputRef.current?.click();
  };

  const upload = async (file: File): Promise<void> => {
    setBusy(true);
    try {
      await client.uploadFile(planId, file, 'just_me', threadId);
      setProblem(null);
    } catch (error) {
      setProblem(fileProblem(error));
    } finally {
      setBusy(false);
    }
  };

  const input = createElement('input', {
    ref: inputRef,
    type: 'file',
    className: 'addFile__input',
    tabIndex: -1,
    'aria-hidden': 'true',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = event.target.files?.[0] ?? null;
      event.target.value = '';
      if (chosen !== null && threadId !== null) {
        void upload(chosen);
      }
    },
  });

  return { input, press, busy, problem };
}
